/**
 * Продавец аккаунта (KAN-611): ссылка на заказ, где брать код входа.
 * KAN-670: с карточкой продавца (seller) - имя ссылкой на страницу покупки,
 * звёзды рейтинга и, если есть, ссылка на заказ. Без карточки - как раньше.
 * KAN-674: editableRating - звёзды кликабельны (строка аккаунта), рейтинг
 * сохраняется в карточку продавца. Без карточки звёзд нет.
 * Небезопасный URL (не http/https) ссылкой не рендерится.
 */
import { RatingStars } from '@/components/telegram/RatingStars';
import { SellerRatingEditor } from '@/components/telegram/SellerRatingEditor';
import { orderNumberFromUrl, safeHttpUrl } from '@/lib/sellerLink';
import type { TelegramSellerBrief } from '@/types/telegramSeller';

interface Props {
  name?: string;
  url?: string;
  seller?: TelegramSellerBrief | null;
  className?: string;
  editableRating?: boolean;
}

const LINK_CLASS = 'text-xs text-blue-600 hover:underline';

function OrderLink({ href, className }: { href: string; className?: string }): JSX.Element {
  const order = orderNumberFromUrl(href);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="seller-order-link"
      className={className ?? LINK_CLASS}
    >
      {`заказ${order ? ` #${order}` : ''} ↗`}
    </a>
  );
}

function SellerCard({
  seller,
  url,
  className,
  editableRating,
}: {
  seller: TelegramSellerBrief;
  url?: string;
  className?: string;
  editableRating?: boolean;
}): JSX.Element {
  const page = safeHttpUrl(seller.url);
  const order = safeHttpUrl(url);
  return (
    <span
      className={`inline-flex flex-wrap items-center gap-1 ${className ?? 'text-xs text-gray-500'}`}
      data-testid="seller-card"
    >
      {page ? (
        <a
          href={page}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="seller-link"
          className={className ?? LINK_CLASS}
        >
          {seller.name}
        </a>
      ) : (
        <span data-testid="seller-name">{seller.name}</span>
      )}
      {editableRating ? (
        <SellerRatingEditor seller={seller} testId="seller-rating" />
      ) : (
        <RatingStars value={seller.rating} testId="seller-rating" />
      )}
      {order && order !== page && (
        <>
          <span>,</span>
          <OrderLink href={order} className={className} />
        </>
      )}
    </span>
  );
}

export function SellerLink({
  name,
  url,
  seller,
  className,
  editableRating,
}: Props): JSX.Element | null {
  if (seller) {
    return (
      <SellerCard
        seller={seller}
        url={url}
        className={className}
        editableRating={editableRating}
      />
    );
  }
  const sellerName = (name ?? '').trim();
  const href = safeHttpUrl(url);
  if (href) {
    const order = orderNumberFromUrl(href);
    const text = `${sellerName || 'Продавец'}, заказ${order ? ` #${order}` : ''} ↗`;
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="seller-link"
        className={className ?? LINK_CLASS}
      >
        {text}
      </a>
    );
  }
  if (sellerName) {
    return (
      <span data-testid="seller-name" className={className ?? 'text-xs text-gray-500'}>
        {sellerName}
      </span>
    );
  }
  return null;
}
